import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type ReactNode,
} from "react";

interface CarouselProps {
  children: ReactNode[];
  autoAdvance?: number; // ms between slides, 0 = disabled
  className?: string;
}

/**
 * Horizontal card slider with prev/next arrows and optional auto-advance.
 */
export function Carousel({
  children,
  autoAdvance = 0,
  className = "",
}: CarouselProps) {
  const [current, setCurrent] = useState(0);
  const total = children.length;
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % total);
  }, [total]);

  const prev = useCallback(() => {
    setCurrent((c) => (c - 1 + total) % total);
  }, [total]);

  // Auto-advance
  useEffect(() => {
    if (autoAdvance <= 0 || total <= 1) return;
    timerRef.current = setInterval(next, autoAdvance);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoAdvance, next, total]);

  // Pause on hover
  const pause = () => {
    if (timerRef.current) clearInterval(timerRef.current);
  };
  const resume = () => {
    if (autoAdvance > 0 && total > 1) {
      timerRef.current = setInterval(next, autoAdvance);
    }
  };

  if (total === 0) return null;

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={pause}
      onMouseLeave={resume}
      aria-roledescription="carousel"
      aria-label="Carousel"
    >
      {/* Slides */}
      <div
        className="flex transition-transform duration-300 ease-in-out"
        style={{ transform: `translateX(-${current * 100}%)` }}
      >
        {children.map((child, i) => (
          <div
            key={i}
            className="w-full shrink-0"
            role="group"
            aria-roledescription="slide"
            aria-label={`Slide ${i + 1} of ${total}`}
          >
            {child}
          </div>
        ))}
      </div>

      {/* Navigation arrows */}
      {total > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-bg-card/80 border border-border text-text-muted hover:text-text-primary transition-colors backdrop-blur-sm"
            aria-label="Previous slide"
          >
            <CaretLeft size={16} />
          </button>
          <button
            onClick={next}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-bg-card/80 border border-border text-text-muted hover:text-text-primary transition-colors backdrop-blur-sm"
            aria-label="Next slide"
          >
            <CaretRight size={16} />
          </button>
        </>
      )}

      {/* Dots */}
      {total > 1 && (
        <div
          className="flex justify-center gap-1.5 mt-3"
          role="tablist"
          aria-label="Slide indicators"
        >
          {children.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              role="tab"
              aria-selected={i === current}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === current
                  ? "w-4 bg-primary"
                  : "w-1.5 bg-border hover:bg-text-muted"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
